import type {
  RoadmapProjectFinalReviewConversation,
  RoadmapProjectFinalReviewConversationMessage,
  RoadmapSegmentConversation,
  RoadmapSegmentConversationMessage,
  RoadmapUseCaseConversation,
  RoadmapUseCaseConversationMessage,
} from "../types/roadmapConversations";

export type RoadmapProject = {
  id: string;
  name: string;
  customerId: string;
  createdAt: string;
  updatedAt: string;
  role?: string | null;
  finalReviewStatus?: string | null;
  firstReviewValidatedAt?: string | null;
  firstReviewValidatedByUserId?: string | null;
  firstReviewForceValidated?: boolean | null;
  firstReviewValidationNotes?: string | null;
};

export type RoadmapRole = "VIEWER" | "EDITOR" | "REVIEWER" | "ADMIN";

export type RoadmapScoringRole = {
  key: string;
  displayName: string;
  factorCount: number;
  memberCount: number;
  memberUserIds?: string[];
  userCanScore: boolean;
};

export type RoadmapRolePermissions = {
  read: boolean;
  edit: boolean;
  review: boolean;
  export: boolean;
  manageMembers?: boolean;
};

export type RoadmapMember = {
  userId: string;
  email: string;
  role: RoadmapRole;
  userRole?: string;
};

export type RoadmapUser = {
  id: string;
  email: string;
  role: string;
};

export type RoadmapSettings = {
  baseId: string;
  processesTable: string;
  processesView?: string | null;
  useCasesTable: string;
  useCasesView?: string | null;
  usersTable?: string | null;
  usersView?: string | null;
  appsTable?: string | null;
  appsView?: string | null;
  integrationsTable?: string | null;
  integrationsView?: string | null;
  technicalAssessmentsTable?: string | null;
  technicalAssessmentsView?: string | null;
  dataRiskChecklistsTable?: string | null;
  dataRiskChecklistsView?: string | null;
  hasToken: boolean;
};

export type RoadmapProcessRecord = {
  id: string;
  airtableRecordId?: string | null;
  airtableBaseId?: string | null;
  name?: string | null;
  matchKey?: string | null;
  reviewOwner?: string | null;
  reviewStatus?: string | null;
  raw?: Record<string, unknown> | null;
};

export type RoadmapUseCaseRecord = {
  id: string;
  airtableRecordId?: string | null;
  airtableBaseId?: string | null;
  name?: string | null;
  linkedProcessKey?: string | null;
  reviewOwner?: string | null;
  reviewStatus?: string | null;
  raw?: Record<string, unknown> | null;
};

export type RoadmapSegmentRecord = {
  id: string;
  name: string;
  kind?: "PLATFORM" | "APPLICATION";
  parentSegmentId?: string | null;
  openQuestionsJson?: string[];
  missingInfoJson?: string[];
  segmentMetaJson?: Record<string, unknown> | null;
  purpose?: string | null;
  solutionType?: string | null;
  aiWhatItDoes?: string | null;
  impact?: string | null;
  draftStatus?: string | null;
  draftMissingInfo?: string[] | null;
  draftEvidenceFieldRefs?: string[] | null;
  draftInputsHash?: string | null;
  draftGeneratedAt?: string | null;
  detailsGeneratedAt?: string | null;
  detailsInputsHash?: string | null;
  detailsStopReason?: string | null;
  detailsMarkdown?: string | null;
  detailsJson?: RoadmapApplicationDetails | null;
  detailsMissingInfo?: string[] | null;
  detailsEvidenceIds?: { useCaseIds?: string[]; processIds?: string[] } | null;
  detailsGeneratedByUserId?: string | null;
  projectId: string;
  useCaseIds?: string[];
};

export type RoadmapSegmentationStrategyPlan = {
  id: string;
  projectId: string;
  status: "DRAFT" | "PROPOSED" | "DISCUSSING" | "APPROVED" | "ARCHIVED";
  stopReason: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL";
  planVersion: number;
  inputsHash: string;
  rulesetVersion: string;
  paramsJson: Record<string, unknown>;
  rationaleMarkdown: string;
  examplesJson: Array<{ useCaseId: string; title: string; why: string }>;
  exceptionsJson: Array<Record<string, unknown>>;
  reasonsJson: RoadmapGateReason[];
  approvedAt?: string | null;
  llmModel?: string | null;
  llmPromptVersion?: string | null;
};

export type RoadmapBlueprintStatus = "DRAFT" | "IN_REVIEW" | "READY" | "NEEDS_APPROVAL" | "APPROVED";

export type RoadmapGateReason = {
  code: string;
  message: string;
};

export type RoadmapApplicationBlueprint = {
  id: string;
  segmentId: string;
  roadmapProjectId: string;
  status: RoadmapBlueprintStatus;
  blueprintJson: Record<string, unknown>;
  blueprintMarkdown?: string | null;
  inputsHash: string;
  approvals: Array<Record<string, unknown>>;
  approvedAt?: string | null;
  approvedByUserId?: string | null;
  createdAt: string;
  updatedAt: string;
  reasonsJson?: RoadmapGateReason[] | null;
};

export type RoadmapBlueprintResponse = {
  ok: boolean;
  stopReason: string;
  blueprint: RoadmapApplicationBlueprint | null;
  reasons?: RoadmapGateReason[];
};

export type RoadmapSuggestionStatus = "DRAFT" | "GENERATED" | "EDITED" | "APPROVED";

export type RoadmapSuggestionParams = {
  startDate: string;
  waveDurationWeeks: number;
  maxParallelApps: number;
  horizonMonths: number;
  capacityAssumptions?: string | null;
};

export type RoadmapSuggestionWaveApplication = {
  segmentId: string;
  name: string | null;
  finalScore: number | null;
  prerequisites: string[];
  rationale: string;
  deliverables: string[];
};

export type RoadmapSuggestionWave = {
  id: string;
  name: string;
  start: string;
  end: string;
  applications: RoadmapSuggestionWaveApplication[];
};

export type RoadmapSuggestionOutput = {
  summary: string;
  assumptions: Array<{ id: string; text: string; impact: string; confidence: number }>;
  constraints: Array<{ id: string; text: string }>;
  risks: Array<{ id: string; text: string; mitigation: string }>;
  waves: RoadmapSuggestionWave[];
  missingInfo: Array<{ id: string; question: string; whyItMatters: string }>;
  confidence: number;
};

export type RoadmapSuggestionRecord = {
  id: string;
  projectId: string;
  status: RoadmapSuggestionStatus;
  stopReason: string;
  inputsHash: string;
  paramsJson: RoadmapSuggestionParams;
  suggestionJson: RoadmapSuggestionOutput;
  approvals: Array<Record<string, unknown>>;
  auditLog: Array<Record<string, unknown>>;
  generatedByUserId?: string | null;
  lastEditedByUserId?: string | null;
  revision: number;
  createdAt: string;
  updatedAt: string;
};

export type RoadmapSuggestionResponse = {
  ok: boolean;
  stopReason: string;
  inputsHash?: string;
  suggestion: RoadmapSuggestionRecord | null;
  reasons?: RoadmapGateReason[];
};

export type RoadmapApplicationAnalysisApplication = {
  id: string;
  name: string;
  useCaseIds: string[];
};

export type RoadmapApplicationsAnalysis = {
  inputsHash: string;
  stopReason: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL";
  createdAt: string;
  applications: RoadmapApplicationAnalysisApplication[];
  evidenceManifest: Record<string, unknown>;
  settings: {
    targetMin: number;
    targetMax: number;
    minUseCasesPerApp: number;
    preferProcessGrouping: boolean;
  };
  summary: {
    useCaseCount: number;
    applicationCount: number;
    minUseCasesPerApp: number;
    medianUseCasesPerApp: number;
    maxUseCasesPerApp: number;
    explanation: string;
  };
};

export type RoadmapApplicationsAnalysisResponse = {
  ok: boolean;
  stopReason: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL";
  analysis: RoadmapApplicationsAnalysis | null;
  reasons?: RoadmapGateReason[];
  applicationsCreated?: number;
  applicationsUpdated?: number;
  applicationsProfiled?: Array<{ id: string; name: string }>;
  assignments?: Array<{ tenantId: string; projectId: string; segmentId: string; useCaseId: string }>;
  digestSummary?: {
    total: number;
    generated: number;
    skipped: number;
    failed: number;
    stopReasonCounts: Record<string, number>;
  };
  synthesisSummary?: {
    total: number;
    processed: number;
    skipped: number;
    failed: number;
    stopReasonCounts: Record<string, number>;
  };
};

export type RoadmapUseCaseDigestRecord = {
  id: string;
  useCaseId: string;
  status: string;
  stopReason: string;
  inputsHash: string;
  updatedAt: string;
};

export type RoadmapUseCaseDigestDetail = RoadmapUseCaseDigestRecord & {
  reasonsJson: Array<Record<string, unknown>>;
  digestJson: Record<string, unknown>;
};

export type RoadmapUseCaseEnrichmentRecord = {
  id: string;
  useCaseId: string;
  status: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL";
  stopReason: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL";
  questionsJson: Array<{
    key: "Q1" | "Q2" | "Q3" | "Q4" | "Q5" | "Q6";
    category: "SEGMENTATION" | "SCORING" | "BOTH";
    question: string;
    rationale: string;
    required: boolean;
  }>;
  answersJson: Record<string, string>;
  inputsHash: string;
  updatedAt: string;
};

export type RoadmapUseCaseDigestSummary = {
  total: number;
  generated: number;
  skipped: number;
  failed: number;
  stopReasonCounts: Record<string, number>;
};

export type RoadmapApplicationDetails = {
  applicationId: string;
  detailMarkdown: string;
  structured: {
    execSummary: string;
    scope: { in: string[]; out: string[] };
    usersAndWorkflow: string;
    dataAndIntegrations: { dataSources: string[]; integrations: string[]; gaps: string[] };
    aiArchitecture: { recommendedApproach: string; tooling: string[]; nonGoals: string[] };
    prereqs: string[];
    librariesToBuild: string[];
    deliveryPlan: Array<{ milestone: string; what: string; risks: string[] }>;
    valueHypothesis: { value: string; confidence: "low" | "med" | "high"; why: string };
    openQuestions: string[];
    evidence: { useCaseIds: string[]; processIds: string[] };
    basis: {
      digest: {
        useCaseIds: string[];
        inputsHashes: string[];
        evidenceFieldRefs: string[];
        coverage: {
          expectedUseCaseIds: string[];
          resolvedUseCaseIds: string[];
          missingUseCaseIds: string[];
          isComplete: boolean;
        };
      };
      synthesis: { useCaseIds: string[]; inputsHashes: string[] };
      segmentation: {
        inputsHash: string | null;
        createdAt: string | null;
        settings: Record<string, unknown>;
        evidenceManifest: Record<string, unknown>;
      };
    };
  };
  stopReason: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL";
  missingInfo: string[];
};

export type RoadmapHandoffResponse = {
  ok: boolean;
  stopReason: string;
  forgeProjectId?: string;
  seedPackHash?: string;
  reasons?: RoadmapGateReason[];
};

export type RoadmapUserRecord = {
  id: string;
  airtableRecordId?: string | null;
  airtableBaseId?: string | null;
  displayName?: string | null;
  raw?: Record<string, unknown> | null;
};

export type RoadmapAppRecord = {
  id: string;
  airtableRecordId?: string | null;
  airtableBaseId?: string | null;
  displayName?: string | null;
  raw?: Record<string, unknown> | null;
};

export type RoadmapIntegrationRecord = {
  id: string;
  airtableRecordId?: string | null;
  airtableBaseId?: string | null;
  displayName?: string | null;
  raw?: Record<string, unknown> | null;
};

export type RoadmapMigrationsRequiredDetails = {
  missingTables: string[];
  suggestedFix: string[];
};

export type RoadmapApiError = Error & {
  code?: string;
  status?: number;
  details?: RoadmapMigrationsRequiredDetails;
};

type RoadmapErrorPayload = {
  ok?: boolean;
  code?: string;
  error?: string;
  details?: RoadmapMigrationsRequiredDetails;
};

function buildRoadmapError(response: Response, payload: RoadmapErrorPayload | null, fallback: string): RoadmapApiError {
  const error = new Error(payload?.error ?? fallback) as RoadmapApiError;
  error.code = payload?.code;
  error.status = response.status;
  if (payload?.details) {
    error.details = payload.details;
  }
  return error;
}

async function readRoadmapJson<T>(response: Response, fallback: string): Promise<T> {
  if (!response.ok) {
    let payload: RoadmapErrorPayload | null = null;
    try {
      payload = (await response.json()) as RoadmapErrorPayload;
    } catch {
      payload = null;
    }
    throw buildRoadmapError(response, payload, fallback);
  }
  return (await response.json()) as T;
}

async function throwRoadmapError(response: Response, fallback: string): Promise<never> {
  let payload: RoadmapErrorPayload | null = null;
  try {
    payload = (await response.json()) as RoadmapErrorPayload;
  } catch {
    payload = null;
  }
  throw buildRoadmapError(response, payload, fallback);
}

export function isMigrationsRequiredError(error: unknown): error is RoadmapApiError & {
  code: "MIGRATIONS_REQUIRED";
  details: RoadmapMigrationsRequiredDetails;
} {
  return error instanceof Error && (error as RoadmapApiError).code === "MIGRATIONS_REQUIRED";
}

export type RoadmapReviewImpactAxis = {
  axis: string;
  definition?: string | null;
};

export type RoadmapReviewGroup = {
  groupType?: string | null;
  title: string;
  summary?: string | null;
  highlights?: string[];
  applicationNames?: string[];
  processes?: Array<{ id: string; name: string | null }>;
  useCases?: Array<{ id: string; name: string | null }>;
  evidenceIds?: string[];
};

export type RoadmapCollaborationGroup = {
  id: string;
  name: string;
  title?: string;
  leadEmail: string;
  memberEmails: string[];
  source?: "atlas_group_collaboration_v1";
};

export type AtlasInvitationEmailRuntime = {
  enabled: boolean;
  mode: "disabled" | "token_outbox" | "webhook";
  provider: "none" | "webhook";
  canSend: boolean;
  maxEmails: number;
  blocker: string | null;
};

export type RoadmapCollaborationProvisioningResult = {
  email: string;
  groups: string[];
  isLead: boolean;
  accountStatus: "existing" | "created" | "invite_only";
  projectMemberStatus: "created" | "updated" | "existing" | "pending_user";
  projectRole: string | null;
  inviteId: string | null;
  setupUrl?: string | null;
  emailDelivery?: {
    status?: "sent" | "outbox_token" | "failed";
    provider?: string;
    reason?: string;
  };
};

export type RoadmapCollaborationProvisioningResponse = {
  ok: boolean;
  summary: {
    targetCount: number;
    createdAccounts: number;
    existingAccounts: number;
    inviteOnly: number;
    projectMembersCreated: number;
    projectMembersUpdated: number;
    projectMembersExisting: number;
    emailSent: number;
    outboxTokens: number;
  };
  emailRuntime: AtlasInvitationEmailRuntime;
  results: RoadmapCollaborationProvisioningResult[];
  productionRuntimeVerified: boolean;
};

export type RoadmapCollaborationRuntimeVerification = {
  ok: boolean;
  status: "blocked" | "ready_with_warnings" | "ready";
  productionRuntimeVerified: boolean;
  requireEmailDelivery: boolean;
  project: { id: string; name: string };
  groups: Array<{ id: string; name: string; leadEmail: string; memberCount: number }>;
  summary: {
    groupCount: number;
    targetEmailCount: number;
    accountCount: number;
    projectMemberCount: number;
    pendingInviteCount: number;
    missingAccountOrInviteCount: number;
    missingProjectMemberCount: number;
  };
  emailRuntime: AtlasInvitationEmailRuntime;
  blockers: string[];
  warnings: string[];
};

export type RoadmapReviewSummarySection = {
  title: string;
  summary: string;
  highlights?: string[];
  relatedGroupTitles?: string[];
  relatedProcessIds?: string[];
  relatedUseCaseIds?: string[];
};

export type RoadmapProjectReview = {
  summary?: string | null;
  impactAxes?: RoadmapReviewImpactAxis[] | null;
  groups?: RoadmapReviewGroup[] | null;
  raw?: Record<string, unknown> | null;
  updatedAt?: string | null;
};

export type RoadmapFinalReview = {
  status: string | null;
  notes: string | null;
  updatedAt?: string | null;
};

export type RoadmapSponsorDossier = {
  schema: "longwalls.atlas-sponsor-dossier@3";
  version: 3;
  generationMode: "deterministic";
  generatedAt: string;
  summary: {
    headline: string;
    sponsorOutcome: string;
    nextMove: string;
  };
  sponsorReadiness: {
    status: "promotable" | "watch" | "blocked";
    rationale: string[];
  };
  recommendation: {
    recommendedApplicationId: string | null;
    recommendedApplicationName: string | null;
    recommendedStackOptionId: string;
    confidence: "high" | "medium" | "watch";
    rationale: string;
    why: string[];
    discussionPrompts: string[];
  };
  stackOptions: Array<{
    id: string;
    label: string;
    posture: "recommended" | "viable" | "watch";
    rationale: string;
    fitSignals: string[];
    keyRisks: string[];
  }>;
  runtimeConstraints: string[];
  hypotheses: Array<{
    id: string;
    title: string;
    status: "assumed" | "validated" | "needs_confirmation";
    rationale: string;
  }>;
  risks: Array<{
    id: string;
    title: string;
    severity: "high" | "medium" | "low";
    rationale: string;
    mitigation: string;
  }>;
  openQuestions: Array<{
    id: string;
    question: string;
    rationale: string;
    blocking: boolean;
    section: "scope" | "runtime" | "data" | "delivery";
  }>;
  promotionChecklist: Array<{
    id: string;
    label: string;
    status: "done" | "missing" | "watch";
    blocking: boolean;
    rationale: string;
    evidence: string[];
  }>;
  foundryExport: {
    status: "ready" | "blocked";
    requiredArtifacts: string[];
    requiredDecisionFields: string[];
    blockingReasonIds: string[];
  };
};

export type RoadmapTechnicalAssessmentRecord = {
  id: string;
  airtableRecordId?: string | null;
  airtableBaseId?: string | null;
  raw?: Record<string, unknown> | null;
};

export type RoadmapDataRiskChecklistRecord = {
  id: string;
  airtableRecordId?: string | null;
  airtableBaseId?: string | null;
  raw?: Record<string, unknown> | null;
};

export type RoadmapProcessUseCaseLink = {
  processId: string;
  useCaseId: string;
};

export type RoadmapLinkSummary = {
  processCount: number;
  useCaseCount: number;
  linkCount: number;
  missingUseCaseLinks: number;
};

export type RoadmapAnalysisFields = {
  processFields: Record<string, boolean>;
  useCaseFields: Record<string, boolean>;
  includeRawFields: boolean;
  processRawFields: string[];
  useCaseRawFields: string[];
};

export type RoadmapQualityAuditIssue = {
  severity: "BLOCKER" | "WARNING";
  entityType: "PROCESS" | "USE_CASE" | "APPLICATION";
  entityId: string;
  entityName?: string | null;
  code: string;
  message: string;
  deepLink?: string | null;
  fieldPath?: string;
  suggestedFix?: string;
  evidenceIds?: string[];
};

export type RoadmapQualityGateOverride = {
  id: string;
  createdAt: string;
  createdByUserId?: string | null;
  createdByEmail?: string | null;
  reason: string;
  reportId?: string | null;
  snapshot: Record<string, unknown>;
  forcePass: boolean;
};

export type RoadmapQualityGateState = {
  status: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL" | null;
  overridden: boolean;
  override: RoadmapQualityGateOverride | null;
};

export type RoadmapReviewViewFieldConfig = {
  key: string;
  label: string;
  enabled: boolean;
  order: number;
};

export type RoadmapReviewViewConfig = {
  processFields: RoadmapReviewViewFieldConfig[];
  useCaseFields: RoadmapReviewViewFieldConfig[];
};

export type RoadmapFirstReviewGate = {
  validatedAt: string | null;
  validatedByUserId: string | null;
  forceValidated: boolean;
  notes: string | null;
};

export type RoadmapFirstReviewValidationDetails = {
  processes: Array<{ id: string; name: string | null; reviewStatus: string | null }>;
  useCases: Array<{ id: string; name: string | null; reviewStatus: string | null }>;
};

export type RoadmapQualityAuditReport = {
  inputsHash: string;
  stopReason: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL";
  createdAt: string;
  summary: {
    processCount: number;
    useCaseCount: number;
    applicationCount: number;
    blockerCount: number;
    warningCount: number;
    readyApplicationsCount: number;
  };
  issues: RoadmapQualityAuditIssue[];
  evidenceManifest: Record<string, unknown>;
  gate?: RoadmapQualityGateState;
};

export type RoadmapSynthesis = {
  inputsHash: string;
  stopReason: "PASS" | "FAIL" | "NO_OP" | "NEEDS_APPROVAL";
  createdAt: string;
  synthesis: {
    summary: string;
    priorities: Array<{
      title: string;
      rationale: string;
      priority: "P1" | "P2" | "P3" | "watch";
      useCaseIds: string[];
    }>;
    risks: string[];
    nextSteps: string[];
    confidence: number | null;
  };
  questionsForFirstReview: Array<{
    question: string;
    rationale?: string;
    entityType?: "PROCESS" | "USE_CASE" | "APPLICATION";
    entityIds?: string[];
  }>;
  duplicatesOrInconsistencies: Array<{
    type: "DUPLICATE" | "INCONSISTENCY";
    description: string;
    entityType?: "PROCESS" | "USE_CASE" | "APPLICATION";
    entityIds?: string[];
  }>;
  evidenceManifest: Record<string, unknown>;
};

export type RoadmapProcessSummary = {
  id: string;
  name: string | null;
  entity: string | null;
  department: string | null;
  status: string | null;
  matchKey?: string | null;
  raw?: Record<string, unknown> | null;
  useCaseCount: number;
  opportunityScore: number | null;
  reviewOwner?: string | null;
  reviewStatus?: string | null;
  updatedAt: string;
  projectId: string;
};

export type RoadmapProcessDetail = {
  id: string;
  name: string | null;
  entity: string | null;
  department: string | null;
  status: string | null;
  purpose: string | null;
  applicability: string | null;
  steps: string[];
  risks: string[];
  systems: string[];
  reviewOwner: string | null;
  reviewNotes: string | null;
  reviewStatus: string | null;
  raw?: Record<string, unknown> | null;
  updatedAt: string;
  projectId: string;
};

export type RoadmapUseCaseSummary = {
  id: string;
  name: string | null;
  status: string | null;
  opportunityScore: number | null;
  reviewOwner?: string | null;
  reviewStatus: string | null;
  updatedAt: string;
};

export type RoadmapAuditEvent = {
  id: string;
  action: string;
  summary: string;
  createdAt: string;
  actorEmail?: string | null;
  actorRole?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type RoadmapUseCaseDetail = {
  id: string;
  name: string | null;
  status: string | null;
  problem: string | null;
  outcome: string | null;
  aiOutputs: string | null;
  humanValidation: string | null;
  acceptanceCriteria: string[];
  notes: string | null;
  aiPriorityScore: number | null;
  opportunityScore: number | null;
  impact: number | null;
  feasibility: number | null;
  interUseCaseBenefit: number | null;
  risk: number | null;
  estimatedDuration: number | null;
  estimatedCost: number | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  businessScore?: number | null;
  itScore?: number | null;
  complianceScore?: number | null;
  standardScoreSummary?: number | null;
  reviewHistory?: Array<{
    stage?: string;
    role?: string;
    actor?: string | null;
    note?: string | null;
    at?: string;
  }> | null;
  reviewOwner?: string | null;
  reviewStatus: string | null;
  reviewNotes: string | null;
  reviewStage?: string | null;
  reviewStageUpdatedAt?: string | null;
  reviewTransitions?: Array<{ from: string; to: string }>;
  jarvisExportStatus?: string | null;
  jarvisExportError?: string | null;
  jarvisExportedAt?: string | null;
  dependencies?: Array<{ id: string; name: string | null; confidence: number; source: string }>;
  dependents?: Array<{ id: string; name: string | null; confidence: number; source: string }>;
  targetArchitecture?: string | null;
  validation?: {
    role: string;
    requiredFields: { key: string; label: string }[];
    missingFields: string[];
    isComplete: boolean;
  } | null;
  auditEvents?: RoadmapAuditEvent[];
  raw?: Record<string, unknown> | null;
  updatedAt: string;
  projectId: string;
};

export type RoadmapUseCaseJarvisExport = {
  customer: string;
  department: string;
  title: string;
};

export type RoadmapUseCaseUpdatePayload = {
  name?: string | null;
  status?: string | null;
  problem?: string | null;
  outcome?: string | null;
  aiOutputs?: string | null;
  humanValidation?: string | null;
  acceptanceCriteria?: string[] | null;
  notes?: string | null;
  opportunityScore?: number | null;
  impact?: number | null;
  feasibility?: number | null;
  risk?: number | null;
  estimatedDuration?: number | null;
  estimatedCost?: number | null;
  plannedStart?: string | null;
  plannedEnd?: string | null;
  reviewOwner?: string | null;
  reviewStatus?: string | null;
  reviewNotes?: string | null;
};

export type RoadmapProcessUpdatePayload = {
  name?: string | null;
  entity?: string | null;
  department?: string | null;
  status?: string | null;
  purpose?: string | null;
  applicability?: string | null;
  steps?: string[] | null;
  risks?: string[] | null;
  systems?: string[] | null;
  reviewOwner?: string | null;
  reviewStatus?: string | null;
  reviewNotes?: string | null;
};

export type RoadmapScoringFactor = {
  id: string;
  projectId: string;
  factor: string;
  weight: number;
  owner?: string | null;
  category?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type RoadmapScoringEntry = {
  id: string;
  projectId: string;
  targetType: "USE_CASE" | "SEGMENT";
  useCaseId?: string | null;
  segmentId?: string | null;
  factorId: string;
  role?: RoadmapRole | null;
  scoringRole?: string | null;
  score: number | null;
  createdAt: string;
  updatedAt: string;
};

export type RoadmapPrioritizedFactor = {
  id: string;
  factor: string;
  weight: number;
  owner?: string | null;
  category?: string | null;
};

export type RoadmapPrioritizedUseCaseFactorScore = {
  factorId: string;
  score: number | null;
  weightedScore: number | null;
  entryCount: number;
};

export type RoadmapPrioritizedUseCase = {
  id: string;
  name: string | null;
  status: string | null;
  opportunityScore: number | null;
  reviewStatus: string | null;
  updatedAt: string;
  aggregatedScore: number | null;
  entryCount: number;
  factorScores: RoadmapPrioritizedUseCaseFactorScore[];
};

export type RoadmapPrioritizedApplicationRoleScore = {
  role: string;
  score: number | null;
  weightedScore: number | null;
  entryCount: number;
};

export type RoadmapPrioritizedApplicationFactorScore = {
  factorId: string;
  score: number | null;
  weightedScore: number | null;
  entryCount: number;
  roleScores: RoadmapPrioritizedApplicationRoleScore[];
};

export type RoadmapPrioritizedApplicationUseCase = {
  id: string;
  name: string | null;
};

export type RoadmapPrioritizedApplication = {
  id: string;
  name: string | null;
  finalScore: number | null;
  entryCount: number;
  useCaseCount: number;
  useCaseIds: string[];
  useCases: RoadmapPrioritizedApplicationUseCase[];
  factorScores: RoadmapPrioritizedApplicationFactorScore[];
};

type RoadmapUseCaseConversationResponse = {
  ok?: boolean;
  conversation?: RoadmapUseCaseConversation;
  messages?: RoadmapUseCaseConversationMessage[];
  message?: RoadmapUseCaseConversationMessage;
  userMessage?: RoadmapUseCaseConversationMessage;
  error?: string;
  useCase?: RoadmapUseCaseDetail;
};

type RoadmapSegmentConversationResponse = {
  ok?: boolean;
  conversation?: RoadmapSegmentConversation;
  messages?: RoadmapSegmentConversationMessage[];
  message?: RoadmapSegmentConversationMessage;
  userMessage?: RoadmapSegmentConversationMessage;
  error?: string;
};

type RoadmapProjectFinalReviewConversationResponse = {
  ok?: boolean;
  conversation?: RoadmapProjectFinalReviewConversation;
  messages?: RoadmapProjectFinalReviewConversationMessage[];
  message?: RoadmapProjectFinalReviewConversationMessage;
  userMessage?: RoadmapProjectFinalReviewConversationMessage;
  error?: string;
};

export async function fetchRoadmapProjects(customerId: string): Promise<RoadmapProject[]> {
  const response = await fetch(`/api/roadmap/projects?customerId=${encodeURIComponent(customerId)}`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap projects");
  }
  const payload = (await response.json()) as { projects?: RoadmapProject[] };
  return payload.projects ?? [];
}

export async function createRoadmapProject(customerId: string, name: string): Promise<RoadmapProject> {
  const response = await fetch("/api/roadmap/projects", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ customerId, name }),
  });
  if (!response.ok) {
    throw new Error("Failed to create roadmap project");
  }
  const payload = (await response.json()) as { project: RoadmapProject };
  return payload.project;
}

export async function fetchRoadmapSettings(projectId: string): Promise<RoadmapSettings | null> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/settings`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap settings");
  }
  const payload = (await response.json()) as { settings?: RoadmapSettings | null };
  return payload.settings ?? null;
}

export async function fetchRoadmapAnalysisFields(projectId: string): Promise<RoadmapAnalysisFields> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/analysis-fields`);
  const payload = await readRoadmapJson<{ analysisFields?: RoadmapAnalysisFields }>(
    response,
    "Failed to load roadmap analysis fields",
  );
  return (
    payload.analysisFields ?? {
      processFields: {},
      useCaseFields: {},
      includeRawFields: true,
      processRawFields: [],
      useCaseRawFields: [],
    }
  );
}

export async function saveRoadmapAnalysisFields(
  projectId: string,
  payload: RoadmapAnalysisFields,
): Promise<RoadmapAnalysisFields> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/analysis-fields`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await readRoadmapJson<{ analysisFields?: RoadmapAnalysisFields }>(
    response,
    "Failed to save roadmap analysis fields",
  );
  if (!data.analysisFields) {
    throw new Error("Failed to save roadmap analysis fields");
  }
  return data.analysisFields;
}

export async function fetchRoadmapReviewViewConfig(projectId: string): Promise<RoadmapReviewViewConfig> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/review-view-config`);
  const payload = await readRoadmapJson<{ config?: RoadmapReviewViewConfig }>(
    response,
    "Failed to load review view config",
  );
  if (!payload.config) {
    throw new Error("Failed to load review view config");
  }
  return payload.config;
}

export async function saveRoadmapReviewViewConfig(
  projectId: string,
  config: RoadmapReviewViewConfig,
): Promise<RoadmapReviewViewConfig> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/review-view-config`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(config),
  });
  const payload = await readRoadmapJson<{ config?: RoadmapReviewViewConfig }>(
    response,
    "Failed to save review view config",
  );
  if (!payload.config) {
    throw new Error("Failed to save review view config");
  }
  return payload.config;
}

export async function validateRoadmapFirstReview(
  projectId: string,
  payload: { force?: boolean; notes?: string } = {},
): Promise<{ ok: boolean; gate?: RoadmapFirstReviewGate; error?: string; details?: RoadmapFirstReviewValidationDetails }> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/first-review/validate`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await readRoadmapJson<{
    ok?: boolean;
    gate?: RoadmapFirstReviewGate;
    error?: string;
    details?: RoadmapFirstReviewValidationDetails;
  }>(response, "Failed to validate 1st review");
  return {
    ok: Boolean(data.ok),
    gate: data.gate,
    error: data.error,
    details: data.details,
  };
}

export async function overrideRoadmapQualityAudit(
  projectId: string,
  reason: string,
): Promise<{ override: RoadmapQualityGateOverride | null; gate: RoadmapQualityGateState | null }> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/quality-audit/override`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  const payload = await readRoadmapJson<{
    override?: RoadmapQualityGateOverride | null;
    gate?: RoadmapQualityGateState | null;
  }>(response, "Failed to override quality gate");
  return { override: payload.override ?? null, gate: payload.gate ?? null };
}

export async function fetchRoadmapQualityAudit(projectId: string): Promise<RoadmapQualityAuditReport | null> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/quality-audit`);
  const payload = await readRoadmapJson<{ report?: RoadmapQualityAuditReport | null }>(
    response,
    "Failed to load quality audit report",
  );
  return payload.report ?? null;
}

export async function runRoadmapQualityAudit(projectId: string): Promise<RoadmapQualityAuditReport> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/quality-audit`, {
    method: "POST",
  });
  const payload = await readRoadmapJson<{ report?: RoadmapQualityAuditReport }>(
    response,
    "Failed to run quality audit",
  );
  if (!payload.report) {
    throw new Error("Failed to run quality audit");
  }
  return payload.report;
}

export async function fetchRoadmapSynthesis(projectId: string): Promise<RoadmapSynthesis | null> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/synthesis`);
  const payload = await readRoadmapJson<{ synthesis?: RoadmapSynthesis | null }>(
    response,
    "Failed to load synthesis report",
  );
  return payload.synthesis ?? null;
}

export async function runRoadmapSynthesis(projectId: string): Promise<RoadmapSynthesis> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/synthesis`, {
    method: "POST",
  });
  const payload = await readRoadmapJson<{ synthesis?: RoadmapSynthesis }>(response, "Failed to run synthesis");
  if (!payload.synthesis) {
    throw new Error("Failed to run synthesis");
  }
  return payload.synthesis;
}

export async function saveRoadmapSettings(
  projectId: string,
  settings: {
    baseId: string;
    processesTable: string;
    processesView?: string;
    useCasesTable: string;
    useCasesView?: string;
    usersTable?: string;
    usersView?: string;
    appsTable?: string;
    appsView?: string;
    integrationsTable?: string;
    integrationsView?: string;
    technicalAssessmentsTable?: string;
    technicalAssessmentsView?: string;
    dataRiskChecklistsTable?: string;
    dataRiskChecklistsView?: string;
    apiToken?: string;
  },
): Promise<RoadmapSettings> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/settings`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(settings),
  });
  if (!response.ok) {
    throw new Error("Failed to save roadmap settings");
  }
  const payload = (await response.json()) as { settings: RoadmapSettings };
  return payload.settings;
}

export async function fetchRoadmapFinalReview(projectId: string): Promise<RoadmapFinalReview> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/final-review`);
  const payload = await readRoadmapJson<{ finalReview?: RoadmapFinalReview | null }>(
    response,
    "Failed to load final review",
  );
  return (
    payload.finalReview ?? {
      status: null,
      notes: null,
      updatedAt: null,
    }
  );
}

export async function fetchRoadmapSponsorDossier(projectId: string): Promise<RoadmapSponsorDossier | null> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/sponsor-dossier`);
  const payload = await readRoadmapJson<{ sponsorDossier?: RoadmapSponsorDossier | null }>(
    response,
    "Failed to load sponsor dossier",
  );
  return payload.sponsorDossier ?? null;
}

export async function fetchRoadmapPrioritizedRoadmap(projectId: string): Promise<{
  factors: RoadmapPrioritizedFactor[];
  applications: RoadmapPrioritizedApplication[];
  useCases: RoadmapPrioritizedUseCase[];
}> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/prioritized`);
  const payload = await readRoadmapJson<{
    factors?: RoadmapPrioritizedFactor[];
    applications?: RoadmapPrioritizedApplication[];
    useCases?: RoadmapPrioritizedUseCase[];
  }>(response, "Failed to load prioritized roadmap");
  return {
    factors: payload.factors ?? [],
    applications: payload.applications ?? [],
    useCases: payload.useCases ?? [],
  };
}

export async function updateRoadmapFinalReview(
  projectId: string,
  payload: { status?: string | null; notes?: string | null },
): Promise<RoadmapFinalReview> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/final-review`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await readRoadmapJson<{ finalReview?: RoadmapFinalReview | null }>(
    response,
    "Failed to update final review",
  );
  return (
    data.finalReview ?? {
      status: null,
      notes: null,
      updatedAt: null,
    }
  );
}

export async function fetchRoadmapProjectFinalReviewConversation(projectId: string): Promise<{
  conversation: RoadmapProjectFinalReviewConversation | null;
  messages: RoadmapProjectFinalReviewConversationMessage[];
}> {
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/final-review/conversation`,
  );
  if (!response.ok) {
    throw new Error("Failed to load final review conversation");
  }
  const payload = (await response.json()) as RoadmapProjectFinalReviewConversationResponse;
  return {
    conversation: payload.conversation ?? null,
    messages: payload.messages ?? [],
  };
}

export async function sendRoadmapProjectFinalReviewConversationMessage(
  projectId: string,
  content: string,
): Promise<{
  conversation: RoadmapProjectFinalReviewConversation | null;
  message: RoadmapProjectFinalReviewConversationMessage;
  messages: RoadmapProjectFinalReviewConversationMessage[];
}> {
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/final-review/conversation`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content }),
    },
  );
  if (!response.ok) {
    throw new Error("Failed to send final review message");
  }
  const payload = (await response.json()) as RoadmapProjectFinalReviewConversationResponse;
  if (!payload.message) {
    throw new Error("Missing final review response message");
  }
  return {
    conversation: payload.conversation ?? null,
    message: payload.message,
    messages: payload.messages ?? [payload.message],
  };
}

export async function importRoadmapFromAirtable(projectId: string): Promise<{
  processRows: { id: string; fields: Record<string, unknown> }[];
  useCaseRows: { id: string; fields: Record<string, unknown> }[];
  userRows: { id: string; fields: Record<string, unknown> }[];
  appRows: { id: string; fields: Record<string, unknown> }[];
  integrationRows: { id: string; fields: Record<string, unknown> }[];
  technicalAssessmentRows: { id: string; fields: Record<string, unknown> }[];
  dataRiskChecklistRows: { id: string; fields: Record<string, unknown> }[];
}> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/import`, {
    method: "POST",
  });
  const payload = (await response.json()) as {
    processRows: { id: string; fields: Record<string, unknown> }[];
    useCaseRows: { id: string; fields: Record<string, unknown> }[];
    userRows?: { id: string; fields: Record<string, unknown> }[];
    appRows?: { id: string; fields: Record<string, unknown> }[];
    integrationRows?: { id: string; fields: Record<string, unknown> }[];
    technicalAssessmentRows?: { id: string; fields: Record<string, unknown> }[];
    dataRiskChecklistRows?: { id: string; fields: Record<string, unknown> }[];
    error?: string;
  };
  if (!response.ok) {
    throw buildRoadmapError(response, payload, "Failed to import from Airtable");
  }
  return {
    processRows: payload.processRows ?? [],
    useCaseRows: payload.useCaseRows ?? [],
    userRows: payload.userRows ?? [],
    appRows: payload.appRows ?? [],
    integrationRows: payload.integrationRows ?? [],
    technicalAssessmentRows: payload.technicalAssessmentRows ?? [],
    dataRiskChecklistRows: payload.dataRiskChecklistRows ?? [],
  };
}

export async function saveRoadmapData(projectId: string, data: {
  processes: { name?: string; matchKey?: string; raw?: Record<string, unknown>; airtableRecordId?: string }[];
  useCases: { name?: string; linkedProcessKey?: string; raw?: Record<string, unknown>; airtableRecordId?: string }[];
  users?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
  apps?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
  integrations?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
  technicalAssessments?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
  dataRiskChecklists?: { raw?: Record<string, unknown>; airtableRecordId?: string }[];
  replaceExisting?: boolean;
}): Promise<void> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/import-data`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to persist roadmap data (HTTP ${response.status}): ${errorBody || "unknown error"}`);
  }
}

export async function fetchRoadmapCollaborationGroups(projectId: string): Promise<RoadmapCollaborationGroup[]> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/collaboration-groups`);
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to load collaboration groups (HTTP ${response.status}): ${errorBody || "unknown error"}`);
  }
  const payload = (await response.json()) as { groups?: RoadmapCollaborationGroup[] };
  return payload.groups ?? [];
}

export async function saveRoadmapCollaborationGroups(
  projectId: string,
  groups: RoadmapCollaborationGroup[],
): Promise<RoadmapCollaborationGroup[]> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/collaboration-groups`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ groups }),
  });
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to save collaboration groups (HTTP ${response.status}): ${errorBody || "unknown error"}`);
  }
  const payload = (await response.json()) as { groups?: RoadmapCollaborationGroup[] };
  return payload.groups ?? [];
}

export async function provisionRoadmapCollaborationGroups(
  projectId: string,
  payload: {
    groupIds?: string[];
    memberRole?: string;
    ownerRole?: string;
    sendEmail?: boolean;
    createAccounts?: boolean;
    createInvites?: boolean;
    reason?: string;
  } = {},
): Promise<RoadmapCollaborationProvisioningResponse> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/collaboration-groups/provision`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to provision collaboration groups (HTTP ${response.status}): ${errorBody || "unknown error"}`);
  }
  return (await response.json()) as RoadmapCollaborationProvisioningResponse;
}

export async function fetchRoadmapCollaborationRuntimeVerification(
  projectId: string,
  requireEmailDelivery = false,
): Promise<RoadmapCollaborationRuntimeVerification> {
  const query = requireEmailDelivery ? "?requireEmailDelivery=true" : "";
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/collaboration-groups/runtime-verification${query}`,
  );
  if (!response.ok) {
    const errorBody = await response.text();
    throw new Error(`Failed to verify collaboration runtime (HTTP ${response.status}): ${errorBody || "unknown error"}`);
  }
  return (await response.json()) as RoadmapCollaborationRuntimeVerification;
}

export async function fetchRoadmapData(projectId: string): Promise<{
  processes: RoadmapProcessRecord[];
  useCases: RoadmapUseCaseRecord[];
  links: RoadmapProcessUseCaseLink[];
  users: RoadmapUserRecord[];
  apps: RoadmapAppRecord[];
  integrations: RoadmapIntegrationRecord[];
  technicalAssessments: RoadmapTechnicalAssessmentRecord[];
  dataRiskChecklists: RoadmapDataRiskChecklistRecord[];
  review: RoadmapProjectReview | null;
  enrichments: RoadmapUseCaseEnrichmentRecord[];
}> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/data`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap data");
  }
  const payload = (await response.json()) as {
    processes?: RoadmapProcessRecord[];
    useCases?: RoadmapUseCaseRecord[];
    links?: RoadmapProcessUseCaseLink[];
    users?: RoadmapUserRecord[];
    apps?: RoadmapAppRecord[];
    integrations?: RoadmapIntegrationRecord[];
    technicalAssessments?: RoadmapTechnicalAssessmentRecord[];
    dataRiskChecklists?: RoadmapDataRiskChecklistRecord[];
    review?: RoadmapProjectReview | null;
    enrichments?: RoadmapUseCaseEnrichmentRecord[];
  };
  return {
    processes: payload.processes ?? [],
    useCases: payload.useCases ?? [],
    links: payload.links ?? [],
    users: payload.users ?? [],
    apps: payload.apps ?? [],
    integrations: payload.integrations ?? [],
    technicalAssessments: payload.technicalAssessments ?? [],
    dataRiskChecklists: payload.dataRiskChecklists ?? [],
    review: payload.review ?? null,
    enrichments: payload.enrichments ?? [],
  };
}

export type RoadmapMappingSuggestion = {
  processLabelField: string;
  processMatchField: string;
  useCaseField: string;
  useCaseProcessField: string;
};

export async function fetchRoadmapMappingSuggestion(projectId: string): Promise<RoadmapMappingSuggestion> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/mapping-suggestions`);
  if (!response.ok) {
    throw new Error("Failed to load mapping suggestions");
  }
  const payload = (await response.json()) as { suggestion: RoadmapMappingSuggestion };
  return payload.suggestion;
}

export async function fetchRoadmapMembers(projectId: string): Promise<{
  members: RoadmapMember[];
  users: RoadmapUser[];
  rolePermissions: Record<RoadmapRole, RoadmapRolePermissions>;
  roleOptions: RoadmapRole[];
}> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/members`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap members");
  }
  const payload = (await response.json()) as {
    members?: RoadmapMember[];
    users?: RoadmapUser[];
    rolePermissions?: Record<RoadmapRole, RoadmapRolePermissions>;
    roleOptions?: RoadmapRole[];
  };
  return {
    members: payload.members ?? [],
    users: payload.users ?? [],
    rolePermissions: payload.rolePermissions ?? ({} as Record<RoadmapRole, RoadmapRolePermissions>),
    roleOptions: payload.roleOptions ?? [],
  };
}

export async function saveRoadmapMember(projectId: string, userId: string, role: RoadmapRole): Promise<RoadmapMember> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/members`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ userId, role }),
  });
  if (!response.ok) {
    throw new Error("Failed to save roadmap member");
  }
  const payload = (await response.json()) as { member: RoadmapMember };
  return payload.member;
}

export async function removeRoadmapMember(projectId: string, userId: string): Promise<void> {
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(userId)}`,
    { method: "DELETE" },
  );
  if (!response.ok) {
    throw new Error("Failed to remove roadmap member");
  }
}

export async function applyRoadmapMapping(
  projectId: string,
  mapping: RoadmapMappingSuggestion,
): Promise<RoadmapLinkSummary> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/apply-mapping`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(mapping),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(payload?.error ?? "Failed to apply mapping");
  }
  const payload = (await response.json()) as { linkSummary: RoadmapLinkSummary };
  return payload.linkSummary;
}

export async function fetchRoadmapProcesses(options: {
  projectId?: string;
  search?: string;
  limit?: number;
  offset?: number;
  sort?: string;
}): Promise<RoadmapProcessSummary[]> {
  const params = new URLSearchParams();
  if (options.projectId) params.set("projectId", options.projectId);
  if (options.search) params.set("search", options.search);
  if (options.limit) params.set("limit", String(options.limit));
  if (options.offset) params.set("offset", String(options.offset));
  if (options.sort) params.set("sort", options.sort);
  const response = await fetch(`/api/roadmap/processes?${params.toString()}`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap processes");
  }
  const payload = (await response.json()) as { processes?: RoadmapProcessSummary[] };
  return payload.processes ?? [];
}

export async function fetchRoadmapProcess(processId: string): Promise<RoadmapProcessDetail> {
  const response = await fetch(`/api/roadmap/processes/${encodeURIComponent(processId)}`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap process");
  }
  const payload = (await response.json()) as { process: RoadmapProcessDetail };
  return payload.process;
}

export async function updateRoadmapProcess(
  processId: string,
  payload: RoadmapProcessUpdatePayload,
): Promise<RoadmapProcessDetail> {
  const response = await fetch(`/api/roadmap/processes/${encodeURIComponent(processId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error ?? "Failed to update roadmap process");
  }
  const data = (await response.json()) as { process: RoadmapProcessDetail };
  return data.process;
}

export async function fetchRoadmapProcessUseCases(processId: string): Promise<RoadmapUseCaseSummary[]> {
  const response = await fetch(`/api/roadmap/processes/${encodeURIComponent(processId)}/use-cases`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap use cases");
  }
  const payload = (await response.json()) as { useCases?: RoadmapUseCaseSummary[] };
  return payload.useCases ?? [];
}

export async function fetchRoadmapUseCase(useCaseId: string): Promise<RoadmapUseCaseDetail> {
  const response = await fetch(`/api/roadmap/use-cases/${encodeURIComponent(useCaseId)}`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap use case");
  }
  const payload = (await response.json()) as { useCase: RoadmapUseCaseDetail };
  return payload.useCase;
}

export async function updateRoadmapUseCase(
  useCaseId: string,
  payload: RoadmapUseCaseUpdatePayload,
): Promise<RoadmapUseCaseDetail> {
  const response = await fetch(`/api/roadmap/use-cases/${encodeURIComponent(useCaseId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update roadmap use case");
  }
  const data = (await response.json()) as { useCase: RoadmapUseCaseDetail };
  return data.useCase;
}

export async function fetchRoadmapUseCaseParentProcess(useCaseId: string): Promise<string | null> {
  const response = await fetch(`/api/roadmap/use-cases/${encodeURIComponent(useCaseId)}/parent-process`);
  if (!response.ok) {
    return null;
  }
  const payload = (await response.json()) as { processId?: string };
  return payload.processId ?? null;
}

export async function exportRoadmapUseCaseToJarvis(useCaseId: string): Promise<{
  export: RoadmapUseCaseJarvisExport;
  useCase: Pick<
    RoadmapUseCaseDetail,
    "jarvisExportStatus" | "jarvisExportError" | "jarvisExportedAt" | "auditEvents"
  >;
}> {
  const response = await fetch(`/api/roadmap/use-cases/${encodeURIComponent(useCaseId)}/jarvis-export`, {
    method: "POST",
  });
  const payload = (await response.json()) as {
    export?: RoadmapUseCaseJarvisExport;
    useCase?: Pick<RoadmapUseCaseDetail, "jarvisExportStatus" | "jarvisExportError" | "jarvisExportedAt">;
    error?: string;
  };
  if (!response.ok || !payload.export || !payload.useCase) {
    throw new Error(payload.error || "Failed to export use case to Jarvis");
  }
  return { export: payload.export, useCase: payload.useCase };
}

export async function exportRoadmapProcessPdf(processId: string): Promise<Blob> {
  const response = await fetch(`/api/roadmap/processes/${encodeURIComponent(processId)}/export.pdf`);
  if (!response.ok) {
    throw new Error("Failed to export roadmap process PDF");
  }
  return response.blob();
}

export type RoadmapBriefAnalysisFields = {
  processFields?: string[];
  useCaseFields?: string[];
  processRawFields?: string[];
  useCaseRawFields?: string[];
  includeRawFields?: boolean;
};

export async function exportRoadmapProjectBriefPdf(
  projectId: string,
  analysisFields?: RoadmapBriefAnalysisFields,
): Promise<Blob> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/brief.pdf`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: analysisFields ? JSON.stringify({ analysisFields }) : undefined,
  });
  if (!response.ok) {
    throw new Error("Failed to export roadmap brief PDF");
  }
  return response.blob();
}

export async function exportRoadmapPrioritizedPdf(projectId: string): Promise<Blob> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/prioritized/export.pdf`);
  if (!response.ok) {
    await throwRoadmapError(response, "Failed to export prioritized roadmap PDF");
  }
  return response.blob();
}

export async function fetchRoadmapUseCaseConversation(useCaseId: string): Promise<{
  conversation: RoadmapUseCaseConversation;
  messages: RoadmapUseCaseConversationMessage[];
}> {
  const response = await fetch(`/api/roadmap/use-cases/${encodeURIComponent(useCaseId)}/conversation`);
  const payload = (await response.json()) as RoadmapUseCaseConversationResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to load conversation (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.conversation) {
    throw new Error("No conversation was returned by the server");
  }

  return {
    conversation: payload.conversation,
    messages: payload.messages ?? [],
  };
}

export async function sendRoadmapUseCaseConversationMessage(
  useCaseId: string,
  content: string,
): Promise<{
  conversation: RoadmapUseCaseConversation;
  message: RoadmapUseCaseConversationMessage;
  messages: RoadmapUseCaseConversationMessage[];
}> {
  const trimmedContent = content.trim();
  if (!trimmedContent) {
    throw new Error("message content is required");
  }

  const response = await fetch(`/api/roadmap/use-cases/${encodeURIComponent(useCaseId)}/conversation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content: trimmedContent }),
  });

  const payload = (await response.json()) as RoadmapUseCaseConversationResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to send conversation message (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.conversation || !payload?.message) {
    throw new Error("Conversation message was not returned by the server");
  }

  return {
    conversation: payload.conversation,
    message: payload.message,
    messages: payload.messages ?? [],
  };
}

export async function fetchRoadmapSegmentConversation(segmentId: string): Promise<{
  conversation: RoadmapSegmentConversation;
  messages: RoadmapSegmentConversationMessage[];
}> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/conversation`);
  const payload = (await response.json()) as RoadmapSegmentConversationResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to load conversation (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.conversation) {
    throw new Error("No conversation was returned by the server");
  }

  return {
    conversation: payload.conversation,
    messages: payload.messages ?? [],
  };
}

export async function sendRoadmapSegmentConversationMessage(
  segmentId: string,
  content: string,
): Promise<{
  conversation: RoadmapSegmentConversation;
  message: RoadmapSegmentConversationMessage;
  messages: RoadmapSegmentConversationMessage[];
}> {
  const trimmedContent = content.trim();
  if (!trimmedContent) {
    throw new Error("message content is required");
  }

  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/conversation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content: trimmedContent }),
  });

  const payload = (await response.json()) as RoadmapSegmentConversationResponse;

  if (!response.ok || payload.ok === false) {
    const message = payload?.error ?? `Failed to send conversation message (status ${response.status})`;
    throw new Error(message);
  }

  if (!payload?.conversation || !payload?.message) {
    throw new Error("Conversation message was not returned by the server");
  }

  return {
    conversation: payload.conversation,
    message: payload.message,
    messages: payload.messages ?? [],
  };
}

export async function validateRoadmapUseCaseIdea(
  useCaseId: string,
  content: string,
): Promise<RoadmapUseCaseDetail> {
  const trimmedContent = content.trim();
  if (!trimmedContent) {
    throw new Error("content is required");
  }

  const response = await fetch(`/api/roadmap/use-cases/${encodeURIComponent(useCaseId)}/validate-idea`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ content: trimmedContent }),
  });

  const payload = (await response.json()) as RoadmapUseCaseConversationResponse;
  if (!response.ok || payload.ok === false || !payload.useCase) {
    const message = payload?.error ?? `Failed to validate idea (status ${response.status})`;
    throw new Error(message);
  }

  return payload.useCase;
}

export async function fetchRoadmapSegments(projectId: string): Promise<RoadmapSegmentRecord[]> {
  const response = await fetch(`/api/roadmap/segments?projectId=${encodeURIComponent(projectId)}`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap segments");
  }
  const payload = (await response.json()) as { segments?: RoadmapSegmentRecord[] };
  return payload.segments ?? [];
}

export async function createRoadmapSegment(
  projectId: string,
  input: {
    name: string;
    purpose?: string;
    solutionType?: string;
    aiWhatItDoes?: string;
    impact?: string;
  },
): Promise<RoadmapSegmentRecord> {
  const response = await fetch("/api/roadmap/segments", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ projectId, ...input }),
  });
  if (!response.ok) {
    throw new Error("Failed to create roadmap segment");
  }
  const payload = (await response.json()) as { segment: RoadmapSegmentRecord };
  return payload.segment;
}

export async function updateRoadmapSegment(
  segmentId: string,
  input: {
    name?: string;
    purpose?: string;
    solutionType?: string;
    aiWhatItDoes?: string;
    impact?: string;
  },
): Promise<RoadmapSegmentRecord> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error("Failed to update roadmap segment");
  }
  const payload = (await response.json()) as { segment: RoadmapSegmentRecord };
  return payload.segment;
}

export async function deleteRoadmapSegment(segmentId: string): Promise<void> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw new Error("Failed to delete roadmap segment");
  }
}


export async function proposeSegmentationStrategy(projectId: string): Promise<{ ok: boolean; plan: RoadmapSegmentationStrategyPlan | null; stopReason: string }> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/segmentation/strategy/propose`, { method: "POST" });
  const payload = (await response.json().catch(() => ({}))) as { ok?: boolean; plan?: RoadmapSegmentationStrategyPlan | null; stopReason?: string; error?: string };
  if (!response.ok) throw new Error(payload.error ?? "Failed to propose segmentation strategy");
  return { ok: Boolean(payload.ok), plan: payload.plan ?? null, stopReason: payload.stopReason ?? "FAIL" };
}

export async function fetchLatestSegmentationStrategy(projectId: string): Promise<RoadmapSegmentationStrategyPlan | null> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/segmentation/strategy/latest`);
  const payload = (await response.json().catch(() => ({}))) as { ok?: boolean; plan?: RoadmapSegmentationStrategyPlan | null };
  if (!response.ok) throw new Error("Failed to load segmentation strategy");
  return payload.plan ?? null;
}

export async function sendSegmentationStrategyMessage(planId: string, message: string): Promise<{ plan: RoadmapSegmentationStrategyPlan | null; assistantReply?: string; rationale?: string; proposedPatchJson?: Record<string, unknown>; stopReason: string; reasons: RoadmapGateReason[] }> {
  const response = await fetch(`/api/roadmap/segmentation/strategy/${encodeURIComponent(planId)}/message`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ message }),
  });
  const payload = (await response.json().catch(() => ({}))) as { plan?: RoadmapSegmentationStrategyPlan | null; assistantReply?: string; rationale?: string; proposedPatchJson?: Record<string, unknown>; stopReason?: string; reasons?: RoadmapGateReason[] };
  if (!response.ok) throw new Error((payload as { error?: string }).error ?? "Failed to send strategy message");
  return {
    plan: payload.plan ?? null,
    assistantReply: payload.assistantReply,
    rationale: payload.rationale,
    proposedPatchJson: payload.proposedPatchJson,
    stopReason: payload.stopReason ?? "FAIL",
    reasons: payload.reasons ?? [],
  };
}

export async function applySegmentationStrategyDiff(planId: string, diffJson: Record<string, unknown>): Promise<RoadmapSegmentationStrategyPlan> {
  const response = await fetch(`/api/roadmap/segmentation/strategy/${encodeURIComponent(planId)}/apply-diff`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ diffJson }),
  });
  const payload = (await response.json().catch(() => ({}))) as { plan?: RoadmapSegmentationStrategyPlan };
  if (!response.ok || !payload.plan) throw new Error("Failed to apply strategy diff");
  return payload.plan;
}

export async function approveSegmentationStrategy(planId: string): Promise<RoadmapSegmentationStrategyPlan> {
  const response = await fetch(`/api/roadmap/segmentation/strategy/${encodeURIComponent(planId)}/approve`, { method: "POST" });
  const payload = (await response.json().catch(() => ({}))) as { plan?: RoadmapSegmentationStrategyPlan };
  if (!response.ok || !payload.plan) throw new Error("Failed to approve strategy plan");
  return payload.plan;
}

export async function runSegmentationV2(projectId: string): Promise<{ ok: boolean; stopReason: string; reasons?: RoadmapGateReason[] }> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/segmentation/v2/run`, { method: "POST" });
  const payload = (await response.json().catch(() => ({}))) as { ok?: boolean; stopReason?: string; reasons?: RoadmapGateReason[] };
  if (!response.ok && response.status !== 403) throw new Error("Failed to run segmentation v2");
  return { ok: Boolean(payload.ok), stopReason: payload.stopReason ?? "FAIL", reasons: payload.reasons };
}

export async function runRoadmapApplicationsAnalysis(
  projectId: string,
  settings?: {
    targetMin: number;
    targetMax: number;
    minUseCasesPerApp: number;
    preferProcessGrouping: boolean;
  },
): Promise<RoadmapApplicationsAnalysisResponse> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/applications/ai-analysis`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ settings }),
  });
  const payload = (await response.json().catch(() => ({}))) as RoadmapApplicationsAnalysisResponse;
  if (!response.ok && response.status !== 412 && response.status !== 409) {
    throw new Error("Failed to analyze applications");
  }
  return payload;
}



export async function generateRoadmapUseCaseEnrichments(
  projectId: string,
  options: { mode?: "all" | "missing_or_changed"; limit?: number } = {},
): Promise<{ ok: boolean; summary: { total: number; processed: number; skipped: number; failed: number; stopReasonCounts: Record<string, number> } }> {
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/use-case-enrichments/generate`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mode: options.mode ?? "missing_or_changed", limit: options.limit }),
    },
  );
  const payload = (await response.json().catch(() => ({}))) as { ok: boolean; summary: { total: number; processed: number; skipped: number; failed: number; stopReasonCounts: Record<string, number> } };
  if (!response.ok) throw new Error("Failed to generate use case enrichments");
  return payload;
}

export async function fetchRoadmapUseCaseEnrichments(projectId: string): Promise<{ ok: boolean; enrichments: RoadmapUseCaseEnrichmentRecord[] }> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/use-case-enrichments`);
  const payload = (await response.json().catch(() => ({}))) as { ok: boolean; enrichments?: RoadmapUseCaseEnrichmentRecord[] };
  if (!response.ok) throw new Error("Failed to load use case enrichments");
  return { ok: payload.ok, enrichments: payload.enrichments ?? [] };
}

export async function updateRoadmapUseCaseEnrichment(
  useCaseId: string,
  answersJson: Record<string, string>,
): Promise<{ ok: boolean; enrichment: RoadmapUseCaseEnrichmentRecord }> {
  const response = await fetch(`/api/roadmap/use-cases/${encodeURIComponent(useCaseId)}/enrichment`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ answersJson }),
  });
  const payload = (await response.json().catch(() => ({}))) as { ok: boolean; enrichment: RoadmapUseCaseEnrichmentRecord };
  if (!response.ok) throw new Error("Failed to update use case enrichment");
  return payload;
}

export async function generateRoadmapUseCaseDigests(
  projectId: string,
  options?: { mode?: "missing_or_changed" | "all"; limit?: number },
): Promise<{ ok: boolean; summary: RoadmapUseCaseDigestSummary }> {
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/use-case-digests/generate`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mode: options?.mode ?? "missing_or_changed", limit: options?.limit }),
    },
  );
  const payload = (await response.json().catch(() => ({}))) as { ok: boolean; summary: RoadmapUseCaseDigestSummary };
  if (!response.ok || payload.ok === false) {
    throw new Error("Failed to generate use case digests");
  }
  return payload;
}

export async function fetchRoadmapUseCaseDigests(
  projectId: string,
): Promise<{ ok: boolean; digests: RoadmapUseCaseDigestRecord[] }> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/use-case-digests`);
  const payload = (await response.json().catch(() => ({}))) as {
    ok: boolean;
    digests?: RoadmapUseCaseDigestRecord[];
  };
  if (!response.ok || payload.ok === false) {
    throw new Error("Failed to load use case digests");
  }
  return { ok: payload.ok, digests: payload.digests ?? [] };
}

export async function fetchRoadmapUseCaseDigestDetail(
  projectId: string,
  useCaseId: string,
): Promise<{ ok: boolean; digest: RoadmapUseCaseDigestDetail }> {
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/use-case-digests/${encodeURIComponent(useCaseId)}`,
  );
  const payload = (await response.json().catch(() => ({}))) as {
    ok: boolean;
    digest?: RoadmapUseCaseDigestDetail;
  };
  if (!response.ok || payload.ok === false || !payload.digest) {
    throw new Error("Failed to load use case digest detail");
  }
  return { ok: payload.ok, digest: payload.digest };
}

export async function generateRoadmapApplicationDetails(
  projectId: string,
  applicationId: string,
  options?: { force?: boolean; autoFill?: boolean },
): Promise<RoadmapBlueprintResponse> {
  const params = new URLSearchParams();
  if (options?.autoFill) {
    params.set("autoFill", "1");
  }
  const query = params.toString();
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/applications/${encodeURIComponent(
      applicationId,
    )}/generate-details${query ? `?${query}` : ""}`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ force: options?.force ?? false }),
    },
  );
  const payload = (await response.json().catch(() => ({}))) as RoadmapBlueprintResponse;
  if (!response.ok) {
    throw new Error("Failed to generate application details");
  }
  return payload;
}

export async function assignRoadmapSegmentUseCase(segmentId: string, useCaseId: string): Promise<void> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/use-cases`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ useCaseId }),
  });
  if (!response.ok) {
    throw new Error("Failed to assign roadmap use case");
  }
}

export async function removeRoadmapSegmentUseCase(segmentId: string, useCaseId: string): Promise<void> {
  const response = await fetch(
    `/api/roadmap/segments/${encodeURIComponent(segmentId)}/use-cases/${encodeURIComponent(useCaseId)}`,
    { method: "DELETE" },
  );
  if (!response.ok) {
    throw new Error("Failed to unassign roadmap use case");
  }
}

export async function fetchRoadmapScoringRoles(projectId: string): Promise<RoadmapScoringRole[]> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/scoring-roles`);
  const payload = await readRoadmapJson<{ roles?: RoadmapScoringRole[] }>(
    response,
    "Failed to load roadmap scoring roles",
  );
  return payload.roles ?? [];
}

export async function updateRoadmapScoringRoleMembers(
  projectId: string,
  roleKey: string,
  userIds: string[],
): Promise<string[]> {
  const response = await fetch(
    `/api/roadmap/projects/${encodeURIComponent(projectId)}/scoring-roles/${encodeURIComponent(roleKey)}/members`,
    {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ userIds }),
    },
  );
  const payload = await readRoadmapJson<{ members?: string[] }>(
    response,
    "Failed to update scoring role members",
  );
  return payload.members ?? [];
}

export async function fetchRoadmapScoringFactors(projectId: string): Promise<RoadmapScoringFactor[]> {
  const response = await fetch(`/api/roadmap/scoring-factors?projectId=${encodeURIComponent(projectId)}`);
  const payload = await readRoadmapJson<{ factors?: RoadmapScoringFactor[] }>(
    response,
    "Failed to load roadmap scoring factors",
  );
  return payload.factors ?? [];
}

export async function createRoadmapScoringFactor(
  projectId: string,
  payload: {
    factor: string;
    weight: number;
    owner?: string | null;
    category?: string | null;
  },
): Promise<RoadmapScoringFactor> {
  const response = await fetch("/api/roadmap/scoring-factors", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ projectId, ...payload }),
  });
  const data = await readRoadmapJson<{ factor: RoadmapScoringFactor }>(
    response,
    "Failed to create roadmap scoring factor",
  );
  return data.factor;
}

export async function updateRoadmapScoringFactor(
  factorId: string,
  payload: Partial<Pick<RoadmapScoringFactor, "factor" | "weight" | "owner" | "category">>,
): Promise<RoadmapScoringFactor> {
  const response = await fetch(`/api/roadmap/scoring-factors/${encodeURIComponent(factorId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await readRoadmapJson<{ factor: RoadmapScoringFactor }>(
    response,
    "Failed to update roadmap scoring factor",
  );
  return data.factor;
}

export async function deleteRoadmapScoringFactor(factorId: string): Promise<void> {
  const response = await fetch(`/api/roadmap/scoring-factors/${encodeURIComponent(factorId)}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    await throwRoadmapError(response, "Failed to delete roadmap scoring factor");
  }
}

export async function fetchRoadmapScoringEntries(
  projectId: string,
  role?: RoadmapRole,
  options?: {
    targetType?: "USE_CASE" | "SEGMENT";
    segmentId?: string;
    useCaseId?: string;
    scoringRole?: string;
  },
): Promise<RoadmapScoringEntry[]> {
  const params = new URLSearchParams({ projectId });
  if (role) params.set("role", role);
  if (options?.targetType) params.set("targetType", options.targetType);
  if (options?.segmentId) params.set("segmentId", options.segmentId);
  if (options?.useCaseId) params.set("useCaseId", options.useCaseId);
  if (options?.scoringRole) params.set("scoringRole", options.scoringRole);
  const response = await fetch(`/api/roadmap/scoring-entries?${params.toString()}`);
  const payload = await readRoadmapJson<{ entries?: RoadmapScoringEntry[] }>(
    response,
    "Failed to load roadmap scoring entries",
  );
  return payload.entries ?? [];
}

export async function saveRoadmapScoringEntry(payload: {
  projectId: string;
  targetType?: "USE_CASE" | "SEGMENT";
  useCaseId?: string;
  segmentId?: string;
  factorId: string;
  role?: RoadmapRole;
  scoringRole?: string;
  score: number;
}): Promise<RoadmapScoringEntry> {
  const response = await fetch("/api/roadmap/scoring-entries", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await readRoadmapJson<{ entry: RoadmapScoringEntry }>(
    response,
    "Failed to save roadmap scoring entry",
  );
  return data.entry;
}

export async function updateRoadmapScoringEntry(
  entryId: string,
  payload: { score: number },
): Promise<RoadmapScoringEntry> {
  const response = await fetch(`/api/roadmap/scoring-entries/${encodeURIComponent(entryId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await readRoadmapJson<{ entry: RoadmapScoringEntry }>(
    response,
    "Failed to update roadmap scoring entry",
  );
  return data.entry;
}

export async function deleteRoadmapScoringEntry(entryId: string): Promise<void> {
  const response = await fetch(`/api/roadmap/scoring-entries/${encodeURIComponent(entryId)}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    await throwRoadmapError(response, "Failed to delete roadmap scoring entry");
  }
}

export async function fetchRoadmapBlueprint(segmentId: string): Promise<RoadmapBlueprintResponse> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/blueprint`);
  if (!response.ok) {
    throw new Error("Failed to load roadmap blueprint");
  }
  return (await response.json()) as RoadmapBlueprintResponse;
}

export async function generateRoadmapBlueprint(segmentId: string): Promise<RoadmapBlueprintResponse> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/blueprint/generate`, {
    method: "POST",
  });
  const payload = (await response.json().catch(() => ({}))) as RoadmapBlueprintResponse;
  if (!response.ok && response.status !== 412 && response.status !== 409) {
    throw new Error("Failed to generate roadmap blueprint");
  }
  return payload;
}

export async function updateRoadmapBlueprint(
  segmentId: string,
  payload: { blueprintJson?: Record<string, unknown>; blueprintMarkdown?: string | null },
): Promise<RoadmapBlueprintResponse> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/blueprint`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error("Failed to update roadmap blueprint");
  }
  return (await response.json()) as RoadmapBlueprintResponse;
}

export async function approveRoadmapBlueprint(segmentId: string): Promise<RoadmapBlueprintResponse> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/blueprint/approve`, {
    method: "POST",
  });
  const payload = (await response.json().catch(() => ({}))) as RoadmapBlueprintResponse;
  if (!response.ok) {
    throw new Error("Failed to approve roadmap blueprint");
  }
  return payload;
}

export async function downloadRoadmapSeedPack(segmentId: string): Promise<Blob> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/seed-pack.zip`);
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string; reasons?: RoadmapGateReason[] };
    const reason = payload.reasons?.[0]?.message;
    throw new Error(payload.error ?? reason ?? "Failed to download seed pack");
  }
  return await response.blob();
}

export async function handoffRoadmapToForge(segmentId: string): Promise<RoadmapHandoffResponse> {
  const response = await fetch(`/api/roadmap/segments/${encodeURIComponent(segmentId)}/handoff-to-forge`, {
    method: "POST",
  });
  const payload = (await response.json().catch(() => ({}))) as RoadmapHandoffResponse;
  if (!response.ok && response.status !== 412 && response.status !== 409) {
    throw new Error("Failed to handoff roadmap application");
  }
  return payload;
}

export async function fetchRoadmapSuggestion(projectId: string): Promise<RoadmapSuggestionResponse> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/roadmap-suggestion`);
  return await readRoadmapJson<RoadmapSuggestionResponse>(response, "Failed to load roadmap suggestion");
}

export async function generateRoadmapSuggestion(
  projectId: string,
  params: Partial<RoadmapSuggestionParams>,
): Promise<RoadmapSuggestionResponse> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/roadmap-suggestion/generate`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(params),
  });
  const payload = (await response.json().catch(() => null)) as RoadmapSuggestionResponse | RoadmapErrorPayload | null;
  if (!response.ok && response.status !== 412 && response.status !== 409) {
    throw buildRoadmapError(response, payload as RoadmapErrorPayload | null, "Failed to generate roadmap suggestion");
  }
  return (payload ?? { ok: false, stopReason: "FAIL", suggestion: null }) as RoadmapSuggestionResponse;
}

export async function updateRoadmapSuggestion(
  projectId: string,
  payload: {
    suggestionJson?: RoadmapSuggestionOutput;
    paramsJson?: RoadmapSuggestionParams;
    changeSummary?: string;
  },
): Promise<RoadmapSuggestionResponse> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/roadmap-suggestion`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  return await readRoadmapJson<RoadmapSuggestionResponse>(response, "Failed to update roadmap suggestion");
}

export async function exportRoadmapSuggestionPdf(projectId: string): Promise<Blob> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/roadmap-suggestion/export.pdf`);
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as
      | (RoadmapErrorPayload & { reasons?: RoadmapGateReason[] })
      | null;
    if (payload?.code) {
      throw buildRoadmapError(response, payload, "Failed to export roadmap suggestion PDF");
    }
    const reason = payload?.reasons?.[0]?.message;
    throw new Error(payload?.error ?? reason ?? "Failed to export roadmap suggestion PDF");
  }
  return await response.blob();
}

export async function exportRoadmapSuggestionZip(projectId: string): Promise<Blob> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/roadmap-suggestion/export.zip`);
  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as
      | (RoadmapErrorPayload & { reasons?: RoadmapGateReason[] })
      | null;
    if (payload?.code) {
      throw buildRoadmapError(response, payload, "Failed to export roadmap suggestion ZIP");
    }
    const reason = payload?.reasons?.[0]?.message;
    throw new Error(payload?.error ?? reason ?? "Failed to export roadmap suggestion ZIP");
  }
  return await response.blob();
}

export async function fetchLatestSegmentationRun(projectId: string): Promise<{ llmModel?: string | null; llmPromptVersion?: string | null; createdAt?: string | null } | null> {
  const response = await fetch(`/api/roadmap/projects/${encodeURIComponent(projectId)}/segmentation/strategy/latest`);
  const payload = (await response.json().catch(() => ({}))) as { plan?: RoadmapSegmentationStrategyPlan | null };
  if (!response.ok) throw new Error("Failed to load segmentation strategy");
  if (!payload.plan) return null;
  return {
    llmModel: payload.plan.llmModel ?? null,
    llmPromptVersion: payload.plan.llmPromptVersion ?? null,
  };
}
